/* „Przypisz plan", variant D — `docs/design/plan-propozycje-2026-09-21/index.html#d/assign/*`.
 *
 * The WINDOW is `shared/ui/splitDialog.module.css`: the grid, the two panes, the head and the foot.
 * What is here is everything inside those panes, and nothing this file states is stated there too —
 * a declaration written on both sides is settled by chunk order, which differs between the dev
 * server and the build, and this repo has already paid for that once.
 *
 * Every colour, space, radius, shadow, duration and type value is a token. Widths, heights and grid
 * tracks are layout and are deliberately outside the gate. */

/* ── A field ────────────────────────────────────────────────────────────────────────────────── */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
}

/* A field the trainer cannot answer yet stays where it is, dimmed. It is not removed: a field that
 * appears once the one above it is answered moves everything below it. */
.fieldLocked {
  opacity: 0.55;
}

.fieldLabel {
  display: flex;
  gap: var(--space-7px);
  align-items: baseline;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.fieldRequired {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.fieldHelp,
.fieldReason {
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* The reason is always in the tree so the id a locked control names never points at nothing. */
.fieldHidden {
  position: fixed;
  inset: 0 auto auto 0;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  clip-path: inset(50%);
}

.note {
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* The mode choice's glyph, beside the shared row's radio. Its ink follows the choice, which the row
 * itself cannot say to a class in another stylesheet — hence its own `On` class, set by `ModeRows`. */
.rowGlyph {
  display: grid;
  place-items: center;
  width: var(--space-5);
  height: var(--space-5);
  color: var(--text-2);
}

.rowGlyphOn {
  color: var(--text);
}

.rowGlyph svg {
  width: var(--space-5);
  height: var(--space-5);
}

/* ── The hours ──────────────────────────────────────────────────────────────────────────────── */

/* A vertical list, one row per programmed weekday — the mock's non-inline `.times`, chosen over
 * its three-across `inline` twin by the owner on 22 September 2026. The label carries both the
 * weekday and the session („Poniedziałek · Push"), so the mock's two left columns are one here and
 * the hour keeps its own fixed width on the right. */
.times {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
}

.time {
  display: grid;
  grid-template-columns: 1fr var(--w-assign-time);
  gap: var(--space-3);
  align-items: center;
  min-height: var(--h-ex-wizard-row);
}

/* The label and, on the fallback slot only, the sentence that says which days it covers. Stacked,
 * so a hint never pushes the hour out of line with the rows above it. */
.timeText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
  min-width: var(--space-0);
}

.timeLabel {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.timeHint {
  font-size: var(--fs-caption);
  color: var(--text-2);
}

.timeInput {
  box-sizing: border-box;
  width: 100%;
  height: var(--h-assign-time);
  padding: var(--space-0) var(--space-10px);
  font: inherit;
  font-size: var(--fs-label-md);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

/* ── The month picker ───────────────────────────────────────────────────────────────────────── */

.picker {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.pickerHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
}

.pickerMonth {
  color: var(--text);
}

.pickerNav {
  display: inline-flex;
  gap: var(--space-1);
}

.pickerStep {
  display: grid;
  place-items: center;
  width: var(--space-6);
  height: var(--space-6);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-7px);
  cursor: pointer;
}

.pickerStep:hover {
  color: var(--text);
  background: var(--hover-tint);
}

.pickerStep svg {
  width: var(--space-11px);
  height: var(--space-11px);
}

.pickerGrid {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
}

.pickerWeekdays,
.pickerRow {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--space-3px);
}

.pickerWeekday {
  padding-bottom: var(--space-1);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-day);

  /* `--text-2`, not `--text-3`: see `.calendarWeekday` below, and `AGENTS.md`'s deviation note. */
  color: var(--text-2);
  text-align: center;
  text-transform: uppercase;
}

.pickerDay {
  position: relative;
  display: grid;
  place-items: center;
  height: var(--h-cal-day);
  font: inherit;
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: transparent;
  border: 0;
  border-radius: var(--radius-8px);
  cursor: pointer;
}

.pickerDay:hover {
  background: var(--hover-tint);
}

.pickerDay:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.pickerDayOutside {
  color: var(--text-2);
  font-weight: var(--fw-medium);
}

.pickerDay[aria-disabled='true'] {
  color: var(--text-2);
  text-decoration: line-through;
  text-decoration-color: var(--border-3);
  cursor: default;
}

/* The chosen start day is a filled accent pill, drawn the way every other accent FILL in this app
 * is: `--accent-grad` under `--on-accent-grad`, the pairing `emberButton` and `saveButton` carry by
 * the owner's directive of 5 August 2026. White ink rather than `--text`, so the pill reads the
 * same in both themes. */
.pickerDayChosen {
  color: var(--on-accent-grad);
  background: var(--primary-fill);

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

.pickerDayChosen:hover {
  background: var(--primary-fill);
}

.pickerNumber {
  line-height: var(--lh-none);
}

/* The dot is what makes this a picker rather than a date input: the trainer can see which days
 * carry a session before choosing one that leaves the first behind. */
.pickerDot {
  position: absolute;
  bottom: var(--space-3px);
  width: var(--space-1);
  height: var(--space-1);
  background: var(--text-2);
  border-radius: var(--r-pill);
}

.pickerDayChosen .pickerDot {
  background: var(--on-accent-grad);
}

/* ── The live panel ─────────────────────────────────────────────────────────────────────────── */

.liveHead {
  display: flex;
  gap: var(--space-3);
  align-items: baseline;
}

.liveTitle {
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
}

.liveSubtitle {
  font-size: var(--fs-note);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.liveSpacer {
  flex: 1;
}

.liveToggle {
  padding: var(--space-0) var(--space-6px);
  font: inherit;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
  background: none;
  border: 0;
  cursor: pointer;
}

/* The ambient state and the calendar stand in the same box, so the swap changes ink and never
 * geometry — the loading policy, applied to a swap the trainer causes. */
.ambient {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6);
  align-items: center;
  justify-content: center;
  padding: var(--space-10) var(--space-60px);
  text-align: center;
}

.ambientViz {
  width: var(--w-viz-ambient);
}

.ambientNote {
  max-width: 40ch;
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.sequenceViz {
  flex: none;
  padding: var(--space-5) var(--space-10) var(--space-2);
}

/* ── The calendar ───────────────────────────────────────────────────────────────────────────── */

.calendar {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.calendarHead,
.calendarRow {
  display: grid;
  grid-template-columns: var(--w-mcal-label) 1fr;
  gap: var(--space-1);
  align-items: stretch;
}

.calendarRail {
  display: grid;
  place-items: center;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.calendarCells {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: var(--space-1);
}

.calendarWeekday {
  padding-bottom: var(--space-2px);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-day);

  /* `--text-2`, although the specimen draws these heads in the tertiary ink. `docs/design/README.md`
   * is the rule that wins: "--text-3 carries no text that must be read", and 2.86:1 on a card is
   * under what a column head at this size owes. The DEVIATION is recorded in this feature's
   * `AGENTS.md` — the same swap is applied to the picker's heads, the struck picker days and a
   * withheld chip, which are the other four places this file put reading matter in `--text-3`. */
  color: var(--text-2);
  text-align: center;
  text-transform: uppercase;
}

.cell {
  position: relative;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
  height: var(--h-mcal-cell);
  padding: var(--space-6px) var(--space-7px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
}

.cellBeforeStart {
  background: transparent;
}

.cellNumber {
  flex: none;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

/* The start date, marked the same way the picker marks the day that set it — one gesture carried
 * from the choice to its consequence. */
.cellStart .cellNumber {
  display: grid;
  place-items: center;
  width: var(--space-5);
  height: var(--space-5);
  margin: var(--offset-cell-number) var(--space-0) var(--space-0) var(--offset-cell-number);
  color: var(--on-accent-grad);
  background: var(--primary-fill);
  border-radius: var(--radius-5px);

  /* White on the accent clears 3:1 only as bold text (system spec amendment, 2 November 2026). */
  font-weight: var(--fw-semibold);
}

/* A cell a drag may land on. Ink, not a tint — the accent marks an ACT, and a hovered target is
 * not one. */
.cellTarget {
  border-color: var(--text);
  box-shadow: var(--shadow-cell-target);
}

.cellRefused {
  border-style: dashed;
  border-color: var(--border-3);
}

/* The stack SCROLLS rather than capping at two with a `+1`: every chip keeps its tab stop and is
 * scrolled into view when it takes focus. See `ProjectionCell`'s header for the departure. */
.cellChips {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2px);
  min-height: var(--space-0);
  overflow-y: auto;
}

/* ── A session chip ─────────────────────────────────────────────────────────────────────────── */

.chip {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-1px);
  padding: var(--space-3px) var(--space-6px);
  font: inherit;
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-normal);
  color: var(--text);
  text-align: left;
  background: var(--bg-inset);
  border: 0;
  border-left: var(--border-ring) solid var(--text);
  border-radius: var(--radius-4px);
  cursor: grab;
}

.chip:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.chipMoved {
  border-left-style: dashed;
}

/* „przeniesiona ze śr." — the line UNDER the title on a moved chip. Its own name, because
 * `.chipMoved` is the button's state class and wearing it here dashed a border the span has not
 * got, which is a rule that does nothing and reads as if it does. */
.chipMovedFrom {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-medium);
  color: var(--text-2);
}

/* Struck out before it is sent: the generator will skip it because it falls before the start. The
 * line through the name is the mark; the colour is not carrying the meaning on its own. */
.chipWithheld {
  color: var(--text-2);
  text-decoration: line-through;
  border-left-color: var(--border-3);
}

.chipLifted {
  box-shadow: var(--shadow-chip-lifted);
}

.chipDragging {
  opacity: 0.4;
}

.chipTime {
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
}

.chipTitle {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  font-size: var(--fs-micro);
  color: var(--text-2);
}

.legendPlanned,
.legendWithheld {
  display: inline-block;
  width: var(--space-10px);
  height: var(--space-10px);
  margin-right: var(--space-5px);
  border-radius: var(--radius-bar);
}

.legendPlanned {
  background: var(--text);
}

.legendWithheld {
  border: var(--border-hairline) dashed var(--border-3);
}

/* ── The self-paced list ────────────────────────────────────────────────────────────────────── */

/* `flex: none` because the live column is a flex column that scrolls: a shrinkable card whose
 * `overflow` is not visible has a min-height of zero, so four cycles were squeezed into the pane's
 * height and each one clipped its own sessions instead of the column scrolling. */
.cycle {
  flex: none;
  overflow: hidden;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-sm);
}

.cycleHeading {
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-3);
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  background: var(--bg-card);
}

.cycleCaption {
  margin: var(--space-0);
  padding: var(--space-0) var(--space-3) var(--space-2);
  font-size: var(--fs-caption);
  background: var(--bg-card);
  color: var(--text-2);
}

.cycleDays {
  display: flex;
  flex-direction: column;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.cycleDay {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: var(--space-3);
  align-items: center;
  padding: var(--space-1) var(--space-2);
  font-size: var(--fs-label-md);
  border-top: var(--border-hairline) solid var(--border);
}

.cyclePosition {
  display: grid;
  place-items: center;
  width: var(--space-7);
  height: var(--space-7);
  font-size: var(--fs-label);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-7px);
}

.cycleText {
  display: flex;
  flex-direction: column;
  gap: var(--space-2px);
}

.cycleTitle {
  font-weight: var(--fw-bold);
}

.cycleSession,
.cycleRest {
  font-size: var(--fs-label);
  color: var(--text-2);
}

.cycleRest {
  white-space: nowrap;
}

/* ── The confirmation ───────────────────────────────────────────────────────────────────────── */

.done {
  display: flex;
  flex-direction: column;
  gap: var(--space-14px);
  align-items: flex-start;
}

.doneMark {
  display: grid;
  place-items: center;
  width: var(--space-11);
  height: var(--space-11);
  color: var(--bg);
  background: var(--text);
  border-radius: var(--radius-12px);
}

.doneMark svg {
  width: var(--space-5);
  height: var(--space-5);
}

.doneTitle {
  margin: var(--space-0);
  font-size: var(--fs-dialog-title);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
}

.doneBody,
.doneSummary {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.doneLinks {
  display: flex;
  gap: var(--space-14px);
  margin-top: var(--space-1);
}

.doneLink,
.doneAgain {
  padding: var(--space-0) var(--space-0);
  font: inherit;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text);
  text-decoration: underline;
  text-underline-offset: var(--space-3px);
  background: none;
  border: 0;
  cursor: pointer;
}

.doneAgain {
  color: var(--text-2);
}

/* ── The popover behind one chip ────────────────────────────────────────────────────────────── */

.editPopup {
  display: flex;
  flex-direction: column;
  gap: var(--space-10px);
  width: var(--w-assign-edit);
  padding: var(--space-3);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
  box-shadow: var(--shadow-modal);
}

.editTitle {
  margin: var(--space-0);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-extrabold);
}

.editField {
  display: flex;
  flex-direction: column;
  gap: var(--space-5px);
}

.editLabel {
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.editInput {
  box-sizing: border-box;
  width: 100%;
  height: var(--h-ex-wizard-row);
  padding: var(--space-0) var(--space-10px);
  font: inherit;
  font-size: var(--fs-label-md);
  font-variant-numeric: tabular-nums;
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

.editRefusal {
  margin: var(--space-0);
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--danger-ink);
}

.editHidden {
  position: fixed;
  inset: 0 auto auto 0;
  width: var(--space-1px);
  height: var(--space-1px);
  overflow: hidden;
  margin: var(--space-0);
  clip-path: inset(50%);
}

.editActions {
  display: flex;
  gap: var(--space-2);
  align-items: center;
}

.editSpacer {
  flex: 1;
}

.editRestore,
.editCancel {
  padding: var(--space-0) var(--space-6px);
  font: inherit;
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  background: none;
  border: 0;
  cursor: pointer;
}

.editRestore:hover,
.editCancel:hover {
  color: var(--text);
}

.editSave {
  height: var(--h-ex-wizard-row);
  padding: var(--space-0) var(--space-14px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);
  border-radius: var(--radius-sm);
}

/* ── The foot ───────────────────────────────────────────────────────────────────────────────── */

/* The plan read's own error state, standing in the box the form would fill — the column keeps its
 * height, so the failure landing changes ink and not geometry. The button wears
 * `shared/ui/ghostButton` for the treatment and `shared/ui/stateAction` for the box, which is the
 * split those two sheets already are. */
.planError {
  display: grid;
  gap: var(--space-2);
  place-items: center;
  align-content: center;
  min-height: var(--h-assign-notice);
  text-align: center;
}

.planErrorMessage {
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

/* ── „Zapisz też jako szablon w bibliotece" ─────────────────────────────────────────────────── */

/* Beside the primary, in the foot — the canvas's place for the unsaved plan's one extra answer. The
 * box is the native checkbox, so its keyboard, its state and its announcement are the platform's;
 * only its tint is ours, and the accent marks the answer once it is given. */
.saveAsTemplate {
  display: inline-flex;
  gap: var(--space-2);
  align-items: center;
  font-size: var(--fs-note);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  cursor: pointer;
}

.saveAsTemplateBox {
  width: var(--space-4);
  height: var(--space-4);
  margin: var(--space-0);
  accent-color: var(--accent);
  cursor: pointer;
}

/* The send is in flight: the answer it carries is already on the wire, so the box is read-only and
 * says so in inert ink rather than toggling into a claim about what was sent. */
.saveAsTemplate:has(.saveAsTemplateBox[aria-disabled='true']) {
  color: var(--text-3);
  cursor: default;
}

.saveAsTemplateBox[aria-disabled='true'] {
  cursor: default;
  accent-color: var(--text-3);
}
